import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Download, Snowflake, Flame, Zap, Leaf, LoaderCircle, LockKeyhole, X } from "lucide-react";
import { FadeUp, MaskedLine } from "@/components/Reveal";
import { categorie, prepLabels } from "@/data/catalogo";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const prepStyle = {
  microonde: { icon: Zap, classes: "bg-moss/10 text-moss" },
  friggitrice: { icon: Flame, classes: "bg-forest/10 text-forest" },
  freddo: { icon: Snowflake, classes: "bg-sage/25 text-forest" },
};

const Catalogo = () => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState(null);

  const handleDownload = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/catalogo-download`, { email });
      const link = document.createElement("a");
      link.href = "/catalog/catalogo-propaq.pdf";
      link.download = "Catalogo-Propaq-B2B.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Download avviato. Il catalogo è tuo.");
      setEmail("");
    } catch (err) {
      toast.error("Qualcosa è andato storto. Riprova.");
    } finally {
      setSending(false);
    }
  };

  const scrollToForm = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
  <div data-testid="catalogo-page" className="pt-20">
    <Seo
      title="Catalogo B2B Propaq — Oltre 90 Referenze Gluten Free | COMMPAQ"
      description="Sfoglia l'anteprima del catalogo Propaq: Kris, fritti, primi, secondi, contorni, sughi e dolci gluten free. Scarica il PDF con i prezzi riservati."
      image="/catalog/cat-kris.jpg"
    />
    <section className="relative overflow-hidden bg-forest py-24 text-bone lg:py-32">
      <p
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 top-1/2 -translate-y-1/2 select-none whitespace-nowrap font-display text-[20vw] font-extrabold text-outline-bone"
      >
        PROPAQ
      </p>
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <p className="flex items-center gap-3 text-sm uppercase tracking-[0.3em] text-sage">
          <Leaf size={18} className="text-moss" />
          Catalogo B2B · by Michelchef srl
        </p>
        <h1
          data-testid="catalogo-heading"
          className="mt-8 font-display text-5xl font-extrabold leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl"
        >
          <MaskedLine delay={0.15}>Piatti & sughi</MaskedLine>
          <MaskedLine delay={0.3} className="text-moss">
            100% gluten free.
          </MaskedLine>
        </h1>
        <FadeUp delay={0.6} className="mt-10 max-w-xl">
          <p className="leading-relaxed text-bone/70">
            Oltre 90 referenze monoporzione pronte in 5 minuti: microonde, friggitrice ad aria o da
            servire fredde. Qui un assaggio: il catalogo completo con i prezzi riservati agli
            operatori si scarica lasciando la tua email.
          </p>
          <form
            data-testid="catalogo-download-form"
            onSubmit={handleDownload}
            className="mt-10 flex max-w-lg flex-col gap-3 sm:flex-row"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="La tua email aziendale"
              data-testid="catalogo-email-input"
              className="w-full rounded-full border border-bone/25 bg-transparent px-6 py-4 text-sm text-bone placeholder:text-bone/40 focus:border-moss focus:outline-none"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="catalogo-download-button"
              className="group inline-flex shrink-0 items-center justify-center gap-3 rounded-full bg-moss px-8 py-4 text-sm uppercase tracking-[0.15em] text-bone transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-ochre hover:text-forest hover:shadow-xl hover:shadow-moss/30 disabled:opacity-60"
            >
              {sending ? (
                <LoaderCircle size={16} className="animate-spin" />
              ) : (
                <>
                  Scarica il PDF
                  <Download size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
                </>
              )}
            </button>
          </form>
          <p className="mt-4 flex items-center gap-2 text-xs text-bone/50">
            <LockKeyhole size={12} />
            Download immediato dopo l'invio della email.
          </p>
        </FadeUp>
      </div>
    </section>

    <div className="sticky top-20 z-40 border-b border-forest/10 bg-bone/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl gap-2 overflow-x-auto px-6 py-4 lg:px-10">
        {categorie.map((c, i) => (
          <a
            key={c.slug}
            href={`#${c.slug}`}
            data-testid={`catalogo-chip-${c.slug}`}
            className="whitespace-nowrap rounded-full border border-forest/15 px-5 py-2 text-xs uppercase tracking-[0.15em] text-forest/70 transition-[background-color,color,border-color] duration-300 hover:border-moss hover:bg-moss hover:text-bone"
          >
            {String(i + 1).padStart(2, "0")} · {c.nome}
          </a>
        ))}
      </div>
    </div>

    {categorie.map((cat, ci) => (
      <section
        key={cat.slug}
        id={cat.slug}
        data-testid={`catalogo-sezione-${cat.slug}`}
        className={`mx-auto max-w-7xl scroll-mt-40 px-6 py-20 lg:px-10 lg:py-28 ${
          ci > 0 ? "border-t border-forest/10" : ""
        }`}
      >
        <FadeUp>
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <span className="font-display text-6xl font-extrabold text-outline">
                {String(ci + 1).padStart(2, "0")}
              </span>
              <h2 className="mt-4 font-display text-4xl font-extrabold leading-none text-forest sm:text-5xl">
                {cat.nome}
              </h2>
              <p className="mt-4 max-w-lg leading-relaxed text-forest/70">{cat.descrizione}</p>
            </div>
            <div className="lg:col-span-4">
              <div className="overflow-hidden rounded-2xl">
                <img
                  src={cat.img}
                  alt={cat.nome}
                  data-testid={`catalogo-img-${cat.slug}`}
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="mt-14 grid grid-cols-1 gap-x-14 md:grid-cols-2">
            {cat.prodotti.slice(0, 4).map((p, pi) => {
              const prep = prepStyle[p.prep];
              return (
                <div
                  key={p.nome}
                  data-testid={`prodotto-${p.nome.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => setSelected({ cat, p, pi })}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && setSelected({ cat, p, pi })}
                  className="group flex cursor-pointer items-center justify-between gap-4 border-b border-forest/10 py-4 transition-colors duration-300 hover:bg-moss/5"
                >
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-forest transition-colors duration-300 group-hover:text-moss">
                        {p.nome}
                      </p>
                      <p className="mt-1 text-xs uppercase tracking-[0.15em] text-forest/50">{p.formato}</p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-4">
                    <span
                      className={`hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] sm:inline-flex ${prep.classes}`}
                    >
                      <prep.icon size={12} />
                      {prepLabels[p.prep]}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </FadeUp>

        {cat.prodotti.length > 4 && (
          <FadeUp delay={0.15}>
            <div
              data-testid={`catalogo-teaser-${cat.slug}`}
              className="mt-6 flex flex-wrap items-center justify-between gap-4 border border-dashed border-forest/25 px-6 py-5"
            >
              <p className="text-sm text-forest/60">
                + altre {cat.prodotti.length - 4} referenze con prezzi riservati nel catalogo completo
              </p>
              <a
                href="#"
                onClick={scrollToForm}
                data-testid={`catalogo-teaser-link-${cat.slug}`}
                className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-moss transition-colors duration-300 hover:text-forest"
              >
                Scarica il catalogo
                <Download size={14} />
              </a>
            </div>
          </FadeUp>
        )}
      </section>
    ))}

    <section className="bg-forest py-24 text-bone lg:py-32">
      <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-10 px-6 md:flex-row md:items-center lg:px-10">
        <FadeUp>
          <h2 className="max-w-xl font-display text-4xl font-extrabold leading-tight sm:text-5xl">
            Vuoi il listino completo <span className="text-moss">con condizioni riservate?</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.15}>
          <Link
            to="/contatti"
            data-testid="catalogo-contact-cta"
            className="inline-flex items-center gap-3 rounded-full bg-ochre px-10 py-4 text-sm uppercase tracking-[0.15em] text-forest transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-ochre/20"
          >
            Contattaci
            <ArrowRight size={16} />
          </Link>
        </FadeUp>
      </div>
    </section>

    <AnimatePresence>
      {selected && (
        <motion.div
          data-testid="prodotto-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-forest/70 p-4 backdrop-blur-sm"
          onClick={() => setSelected(null)}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-lg overflow-hidden rounded-2xl bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col p-8">
              <button
                onClick={() => setSelected(null)}
                data-testid="prodotto-modal-close"
                aria-label="Chiudi"
                className="self-end rounded-full border border-forest/15 p-2 text-forest transition-colors duration-300 hover:border-moss hover:text-moss"
              >
                <X size={18} />
              </button>
              <p className="mt-2 text-xs uppercase tracking-[0.2em] text-moss">{selected.cat.nome}</p>
              <h3 className="mt-3 font-display text-3xl font-extrabold leading-tight text-forest">
                {selected.p.nome}
              </h3>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-forest/10 px-4 py-1.5 text-[11px] uppercase tracking-[0.12em] text-forest">
                  {selected.p.formato}
                </span>
                <span className="rounded-full bg-moss/10 px-4 py-1.5 text-[11px] uppercase tracking-[0.12em] text-moss">
                  {prepLabels[selected.p.prep]}
                </span>
              </div>
              <p className="mt-6 text-sm leading-relaxed text-forest/70">
                {selected.cat.descrizione} Monoporzione 100% gluten free di Michelchef srl,
                pronta in 5 minuti.
              </p>
              <div className="mt-auto pt-8">
                <p className="text-xs text-forest/50">Il prezzo è riservato agli operatori.</p>
                <a
                  href="#"
                  onClick={(e) => {
                    setSelected(null);
                    scrollToForm(e);
                  }}
                  data-testid="prodotto-modal-cta"
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-moss px-7 py-3.5 text-xs uppercase tracking-[0.15em] text-bone transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-forest"
                >
                  Scarica il listino completo
                  <Download size={14} />
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
  );
};

export default Catalogo;
